import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No verification token provided. Please check your email link.');
        return;
      }

      try {
        const response = await axios.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setMessage(
          error.response?.data?.detail || 
          'Failed to verify email. The link may have expired.'
        );
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#0F766E] flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-bold font-['Manrope']" style={{color: '#0f172a'}}>
            ClinicalSim AI
          </span>
        </div>

        {/* Loading State */}
        {status === 'loading' && (
          <div className="py-8">
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-[#0F766E] mb-4" />
            <p className="text-lg text-slate-600">{message}</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="py-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold font-['Manrope'] mb-2" style={{color: '#0f172a'}}>
              Email Verified!
            </h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-[#0F766E] hover:bg-[#0d6960] text-white px-8"
            >
              Go to Login
            </Button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="py-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold font-['Manrope'] mb-2" style={{color: '#0f172a'}}>
              Verification Failed
            </h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/auth')} 
                className="bg-[#0F766E] hover:bg-[#0d6960] text-white w-full"
              >
                Go to Login
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/')} 
                className="w-full border-slate-300"
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
